export const DEFAULT_TEST_CONCURRENCY = 2;
export const DEFAULT_TEST_START_INTERVAL_MS = 1_000;
export const TEST_RATE_LIMIT_COOLDOWN_MS = 60_000;

export type TestQueueState = { active: number; queued: number; pausedUntil: number };

/** Pace test starts as well as bounding in-flight evaluations. Scoped to one app instance. */
export function createEvaluationTestQueue(onChange: (state: TestQueueState) => void = () => {}) {
  let concurrency = DEFAULT_TEST_CONCURRENCY;
  let startIntervalMs = DEFAULT_TEST_START_INTERVAL_MS;
  let active = 0;
  let lastStart: number | null = null;
  let pausedUntil = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const pending: { start: () => Promise<void> }[] = [];
  const publish = () => onChange({ active, queued: pending.length, pausedUntil });

  const pump = () => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
    if (!pending.length || active >= concurrency) return;
    const wait = Math.max(pausedUntil, lastStart === null ? 0 : lastStart + startIntervalMs) - Date.now();
    if (wait > 0) {
      timer = setTimeout(pump, wait);
      return;
    }
    const job = pending.shift()!;
    active++;
    lastStart = Date.now();
    publish();
    void job.start().finally(() => {
      active--;
      publish();
      pump();
    });
    pump();
  };

  return {
    configure(nextConcurrency: unknown, nextStartIntervalMs: unknown) {
      concurrency = typeof nextConcurrency === "number" && Number.isFinite(nextConcurrency)
        ? Math.max(1, Math.min(10, Math.floor(nextConcurrency))) : DEFAULT_TEST_CONCURRENCY;
      startIntervalMs = typeof nextStartIntervalMs === "number" && Number.isFinite(nextStartIntervalMs)
        ? Math.max(1_000, Math.min(60_000, nextStartIntervalMs)) : DEFAULT_TEST_START_INTERVAL_MS;
      pump();
      return { concurrency, startIntervalMs };
    },
    pause() {
      pausedUntil = Math.max(pausedUntil, Date.now() + TEST_RATE_LIMIT_COOLDOWN_MS);
      publish();
      pump();
    },
    add<T>(task: () => Promise<T>): Promise<T> {
      return new Promise<T>((resolve, reject) => {
        pending.push({ start: async () => {
          try { resolve(await task()); }
          catch (error) { reject(error); }
        } });
        publish();
        pump();
      });
    },
  };
}

export function isTestRateLimitError(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const error = value as { status?: unknown; statusCode?: unknown; message?: unknown; errorMessage?: unknown };
  if ([429, 529].includes(Number(error.statusCode ?? error.status))) return true;
  const message = error.errorMessage ?? error.message;
  return typeof message === "string" && /rate[\s_-]*limit|too many requests|\b429\b|\b529\b|overloaded/i.test(message);
}
